/**
 * usePushSubscription — registra Service Worker + Web Push subscription
 * (PRD 1, Story 1.6.3).
 *
 * Fluxo de subscribe (chamado por user gesture, ex: clique em "Ativar
 * notificações"):
 *
 *   1. Pede `Notification.requestPermission()` (só o clique pede; `inscreverEsteAparelho` não pede).
 *   2. `inscreverEsteAparelho({ basePath, origem: "clique" })` (`lib/push/inscreverEsteAparelho.ts`),
 *      a rotina única que o religar sozinho e a renovação também usam: registra `/service-worker.js`
 *      se preciso, espera o SW ficar pronto, busca a chave VAPID (GET /push/vapid-key),
 *      `registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey })` e
 *      POST /push/subscribe com `subscription.toJSON()` + a `origem`.
 *   3. Dentro dela, só depois de o POST dar certo: marca este app como dono do push do aparelho
 *      (`lib/push/donoDoPush.ts`), grava a intenção `ligado` e a confirmação do servidor
 *      (`lib/push/memoriaDoPush.ts`).
 *
 * Dono do push (decisão do Augusto, 12/10/2026: "só num clique"): o SE_WEB interno e o Portal do
 * Parceiro dividem a origem e o service worker, logo a inscrição do navegador, e o servidor dá a
 * linha a quem postou por último. `subscribe()` (ligar no cartão/convite) é um dos dois gestos que
 * trocam o dono — o outro é o login; só ele grava a marca depois de um POST que deu certo. O
 * `unsubscribe()` que termina bem larga a marca, se era deste app, e grava a intenção `desligado`
 * (só depois de o `unsubscribe()` do navegador dar certo: é a marca de que a pessoa quis desligar, e
 * é ela que impede o religar sozinho de desfazer o Desligar). Com a marca do OUTRO app ele não
 * faz nada (a inscrição do navegador é uma só: desligá-la calaria o push do outro app).
 *
 * Por que o hook NÃO subscreve sozinho:
 *   Notification.requestPermission() PRECISA de user gesture (clique). Browsers
 *   modernos rejeitam silenciosamente a chamada se ela vier de um effect/mount.
 *   Então o hook expõe `subscribe()` e a UI decide quando chamar. O religar com a permissão JÁ dada
 *   (que não precisa de gesto) vive em `lib/push/sincronizarPush.ts`, fora daqui.
 *
 * O hook relê a permissão e a inscrição quando o evento `seweb:push-mudou` chega (algo mudou o push
 * deste aparelho por fora dele: o religar sozinho, outra aba). Ouvir o evento nunca dispara outra
 * sincronização.
 *
 * Dois clientes HTTP, escolhidos pelo `basePath` (Task 6b, achado de 18/09):
 *   o parceiro do Portal autentica com token PRÓPRIO (`portal_access_token`),
 *   nunca com o `seweb_auth` do sistema interno — usar o cliente errado faz
 *   o Portal tomar 401 e, pior, desloga sessão interna que porventura exista
 *   no mesmo navegador. A escolha do cliente (e os dois basePaths) mora em
 *   `clienteDoPush.ts` (Task 6c) — o MESMO lugar que `useServerHasVapid.ts`
 *   usa, pra nunca mais divergir. Ver a docstring de lá para o achado completo.
 *
 * Refs:
 *   - W3C Push API: https://www.w3.org/TR/push-api/
 *   - Backend endpoints: V1/backend/app/api/v1/endpoints/push.py
 *   - Story spec: docs/MODULO_EXPANSAO_2026/stories/EPICS-STORIES-PRD-1-* §1.6.3
 */
import { useCallback, useEffect, useReducer, useState } from "react";

import { appDoPush, podeRenovarOPush, soltarDonoDoPush } from "@/lib/push/donoDoPush";
import { SemChaveVapidError, inscreverEsteAparelho } from "@/lib/push/inscreverEsteAparelho";
import {
  EVENTO_PUSH_MUDOU,
  lerIntencaoDoPush,
  limparIntencaoDoPush,
  registrarIntencaoDoPush,
} from "@/lib/push/memoriaDoPush";

import { PUSH_BASE_INTERNO, PUSH_BASE_PORTAL, clienteDoPush } from "./clienteDoPush";

const SERVICE_WORKER_URL = "/service-worker.js";

export type PushPermission = "default" | "granted" | "denied";

export interface UsePushSubscriptionState {
  /** SW + Push API + Notification API disponíveis no navegador. */
  isSupported: boolean;
  /** Estado atual de Notification.permission (ou "default" se não suportado). */
  permission: PushPermission;
  /** Subscription ativa (se houver). null = não subscrito ou ainda checando. */
  subscription: PushSubscription | null;
  /** Operação subscribe() em andamento. */
  isSubscribing: boolean;
  /** Operação unsubscribe() em andamento. */
  isUnsubscribing: boolean;
  /**
   * Há inscrição no navegador, mas o push do aparelho está com o OUTRO app (marca `seweb:push-dono`). O navegador tem uma inscrição só
   * para os dois apps: `subscription` aparece aqui mesmo que os avisos estejam indo para o outro, então a UI que mostra "ligado"
   * só com `subscription` engana, e o `unsubscribe()` mataria, calado, o push do outro app. Passa a `false` quando este app toma o
   * push (`subscribe()` que deu certo) ou quando a inscrição some.
   */
  pushEmOutroApp: boolean;
  /** Última mensagem de erro (limpa em cada nova chamada). */
  error: string | null;
  /** Solicita permissão + cria subscription + persiste no backend. */
  subscribe: () => Promise<PushSubscription | null>;
  /** Remove subscription local + DELETE no backend. Não faz nada se o push do aparelho está com o OUTRO app. */
  unsubscribe: () => Promise<void>;
}

function detectSupport(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

function readPermission(): PushPermission {
  if (typeof window === "undefined" || !("Notification" in window)) return "default";
  return Notification.permission as PushPermission;
}

// Reexportados por compatibilidade: NotificationPrompt.tsx e PortalLayout.tsx
// importam PUSH_BASE_INTERNO/PUSH_BASE_PORTAL a partir DESTE módulo. A fonte
// canônica agora é clienteDoPush.ts (Task 6c) — ver lá o porquê.
export { PUSH_BASE_INTERNO, PUSH_BASE_PORTAL };

export function usePushSubscription(
  basePath: string = PUSH_BASE_INTERNO
): UsePushSubscriptionState {
  const [isSupported] = useState<boolean>(detectSupport);
  const [permission, setPermission] = useState<PushPermission>(readPermission);
  const [subscription, setSubscription] = useState<PushSubscription | null>(null);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isUnsubscribing, setIsUnsubscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Só força um novo render (a marca do dono mora fora do React): ver a recusa em `unsubscribe`.
  const [, recalcular] = useReducer((n: number) => n + 1, 0);

  // Qual cliente HTTP falar: ver clienteDoPush.ts. Derivado a cada render —
  // puro, barato, e sempre em dia com `basePath` (que já está nas deps de
  // subscribe/unsubscribe abaixo).
  const httpClient = clienteDoPush(basePath);

  // Em mount, reflete subscription existente (caso usuário já tenha aceitado antes — ex: voltou ao app após reload). E de novo a cada
  // `seweb:push-mudou`: algo mudou o push deste aparelho por fora do hook (o religar sozinho, outra aba) e a tela precisa reler o
  // navegador. O ouvinte só lê: nunca dispara outra sincronização (nem emite o evento), então não há cascata.
  useEffect(() => {
    if (!isSupported) return;
    let cancelled = false;
    const lerInscricao = async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_URL);
        if (!reg || cancelled) return;
        const existing = await reg.pushManager.getSubscription();
        if (!cancelled) setSubscription(existing);
      } catch {
        // sem registro = estado inicial; não trata como erro
      }
    };
    const aoMudarOPush = () => {
      setPermission(readPermission());
      // A marca do dono mora fora do React: quem a gravou (o `inscreverEsteAparelho` de outro hook, o login) não re-renderizou este.
      recalcular();
      void lerInscricao();
    };
    void lerInscricao();
    window.addEventListener(EVENTO_PUSH_MUDOU, aoMudarOPush);
    return () => {
      cancelled = true;
      window.removeEventListener(EVENTO_PUSH_MUDOU, aoMudarOPush);
    };
  }, [isSupported]);

  const subscribe = useCallback(async (): Promise<PushSubscription | null> => {
    if (!isSupported) {
      setError("Navegador não suporta Web Push");
      return null;
    }
    setIsSubscribing(true);
    setError(null);
    try {
      const requested = await Notification.requestPermission();
      setPermission(requested as PushPermission);
      if (requested !== "granted") {
        setError("Permissão de notificação negada");
        return null;
      }

      // O clique é quem desfaz o Desligar (Decisão 3: `desligado` só sai num clique de Ligar), e o desfaz ANTES de inscrever: entre o
      // `subscribe()` e o POST já há inscrição no navegador e o `ligado` ainda não foi gravado, e o `sincronizarPush` que visse
      // `desligado` nessa janela (desta aba ou de outra) desfaria a inscrição que a pessoa acabou de pedir. Vazia, a janela só deixa a
      // sincronização adotar o mesmo endpoint (upsert inofensivo). Se o clique falhar daqui para a frente, a intenção fica vazia e vale a
      // regra do aparelho sem intenção (custo aceito, Ruling 7). Permissão negada ou navegador sem suporte saem antes e não a tocam.
      if (lerIntencaoDoPush() === "desligado") limparIntencaoDoPush();

      // A rotina única: SW, chave VAPID, subscribe e POST; e, só depois de o POST dar certo, dono + intenção `ligado` + confirmação.
      const sub = await inscreverEsteAparelho({ basePath, origem: "clique" });
      setSubscription(sub);
      return sub;
    } catch (e) {
      setError(
        e instanceof SemChaveVapidError
          ? "Servidor sem chave VAPID configurada"
          : e instanceof Error
            ? e.message
            : String(e)
      );
      return null;
    } finally {
      setIsSubscribing(false);
    }
  }, [isSupported, basePath]);

  const unsubscribe = useCallback(async (): Promise<void> => {
    if (!subscription) return;
    // Guarda CENTRAL (C5): o navegador tem UMA inscrição para o SE_WEB e o Portal; `subscription.unsubscribe()` a mata para os dois. Com
    // a marca do OUTRO app, desligar aqui calaria o push dele. A marca é lida NA HORA do clique, não a do render: uma aba com o estado
    // desatualizado (cartão aberto em duas abas) ainda mostra "ligado" + Desligar depois que a outra tomou o push. Recusa sem DELETE, sem
    // `unsubscribe()` do navegador, sem soltar marca e sem erro na tela; só recalcula o estado derivado, para a tela se corrigir.
    if (!podeRenovarOPush(appDoPush(basePath))) {
      recalcular();
      return;
    }
    setIsUnsubscribing(true);
    setError(null);
    try {
      // Backend primeiro: se a subscription já está morta no servidor, o
      // cleanup job (Story 1.6.2) já cuidou; só ignoramos 404. Em qualquer
      // outro erro, abortamos antes de mexer no client (idempotência).
      try {
        await httpClient.delete(`${basePath}/subscribe`, {
          data: { endpoint: subscription.endpoint },
        });
      } catch (e: any) {
        if (e?.response?.status !== 404) throw e;
      }
      await subscription.unsubscribe();
      // Só aqui, com o `unsubscribe()` do navegador feito: a pessoa quis desligar (é a marca que o religar sozinho respeita). Falhou
      // antes (recusa, DELETE 500, erro do navegador)? Cai no `catch` sem passar por aqui, e a intenção segue como estava.
      registrarIntencaoDoPush("desligado");
      // Push desligado neste aparelho: larga a marca, se era deste app (a do outro app fica com ele), e esquece a confirmação, o
      // carimbo e as falhas do app (o `soltarDonoDoPush` as apaga).
      soltarDonoDoPush(appDoPush(basePath));
      setSubscription(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
    } finally {
      setIsUnsubscribing(false);
    }
  }, [subscription, basePath, httpClient]);

  // Derivado a cada render (uma leitura do localStorage): tudo o que muda a marca ou a inscrição aqui — a carga da inscrição na
  // montagem, o fim do `subscribe()`, o do `unsubscribe()` e o evento `seweb:push-mudou` — termina em setState (ou `recalcular`) e
  // re-renderiza depois de gravar a marca.
  const pushEmOutroApp = subscription !== null && !podeRenovarOPush(appDoPush(basePath));

  return {
    isSupported,
    permission,
    subscription,
    isSubscribing,
    isUnsubscribing,
    pushEmOutroApp,
    error,
    subscribe,
    unsubscribe,
  };
}
